import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedUserAuthTagList } from "../services/authTagService";

interface IUseFetchAuthTags {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchTerm?: string;
  isActive?: string;
}

const usePaginatedFetchAuthTagsList = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  isActive = "true",
}: IUseFetchAuthTags) => {
  const [authTagsData, setAuthTagsData] = useState<any>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedUserAuthTagList({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setAuthTagsData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setAuthTagsData({});
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [authTagsData, fetchData];
};

export default usePaginatedFetchAuthTagsList;
